import { Form, FormProps, useStore } from "@/components/form";
import { z } from "zod/v4";
import { addClienteFormSchema } from "./add-cliente-form.schema";
import { NaturalezaReceptorEnum, TipoContribuyenteEnum, TipoDocumentoIdentidadEnum, TipoOperacionEnum } from "@/shared/types/contribuyente";
import Stack from "@mui/material/Stack/Stack";
import Typography from "@mui/material/Typography/Typography";
import SearchIcon from '@mui/icons-material/Search';
import { Alert, CircularProgress, Collapse, IconButton } from "@mui/material";
import { Dialog, ModalContextExtractor, ModalHeader, useModalContext, useOpenModal } from "@/components/modal";
import { useEffect, useState } from "react";
import useFetch from "@/hooks/use-fetch";
import { checkCliente } from "../api/check-cliente";
import { Departamento } from "@/shared/types/departamentos";
import { Ciudad } from "@/shared/types/ciudad";
import { getPais } from "../../pais/api/get-pais";
import { getAllPaises } from "../../pais/api/get-all-paises";
import CiudadManualSearch from "../../ciudades/components/ciudad-manual-search";
import DepartamentoManualSearch from "../../departamentos/components/departamento-manual-search";

export default function EditClienteForm({
    form,
    ...props
}: FormProps<z.input<typeof addClienteFormSchema>>) {

    const openModal = useOpenModal();
    const [esContribuyente, setEsContribuyente] = useState(() => form.getFieldValue("naturaleza") === NaturalezaReceptorEnum.Contribuyente);
    const [tipoDocumento, setTipoDocumento] = useState(() => form.getFieldValue("tipoDocumento") ?? TipoDocumentoIdentidadEnum.CedulaParaguaya);

    const { fetch, value, loading } = useFetch(async (ruc: string) => {
        if (ruc) return await checkCliente(ruc);
        return undefined;
    }, { debounceMs: 500 });

    const handleDepartamentoSelect = (departamento: Departamento) => {
        form.resetField("departamentoId");
        form.setFieldValue("departamentoId", departamento.id);
        form.setFieldValue("departamentoSeleccionado", departamento.nombre);
        form.setFieldValue("ciudadId", "");
        form.setFieldValue("ciudadSeleccionada", "");
    };

    const handleCiudadSelect = (ciudad: Ciudad) => {
        form.resetField("ciudadId");
        form.setFieldValue("ciudadId", ciudad.id);
        form.setFieldValue("ciudadSeleccionada", ciudad.nombre);
    };

    const handleNaturalezaChange = (value: number) => {
        const isContrib = value === NaturalezaReceptorEnum.Contribuyente;
        setEsContribuyente(isContrib);

        if (isContrib) {
            form.setFieldValue("numeroDocumento", "");
            form.resetField("numeroDocumento");
        } else {
            form.setFieldValue("ruc", "");
            form.setFieldValue("dv", "");
            form.resetField("ruc");
            form.resetField("dv");
        }
    };

    const handleTipoDocumentoChange = (value: number) => {
        setTipoDocumento(value);

        if (value === TipoDocumentoIdentidadEnum.Innominado) {
            form.setFieldValue("numeroDocumento", "0");
        } else {
            if (form.getFieldValue("numeroDocumento") === "0") {
                form.setFieldValue("numeroDocumento", "");
            }
        }
    };

    useEffect(() => {
        const setDefaults = async () => {
            const currentPaisId = form.getFieldValue("paisId");
            const currentCodigoPais = form.getFieldValue("codigoPais");

            if (currentPaisId && currentCodigoPais) return;
        };
        setDefaults();
    }, [form]);

    // Sincroniza codigoPais cuando paisId cambia
    const paisId = useStore(form.store, state => state.values.paisId);

    useEffect(() => {
        if (!paisId) return;

        const syncCodigoPais = async () => {
            try {
                const pais = await getPais(paisId);
                if (pais?.codigo) {
                    form.setFieldValue("codigoPais", pais.codigo);
                }
            } catch (error) {
                console.error("Error fetching pais:", error);
            }
        };

        syncCodigoPais();
    }, [paisId]);

    const naturalezaValue = form.getFieldValue("naturaleza");
    const tipoDocumentoValue = form.getFieldValue("tipoDocumento");

    useEffect(() => {
        setEsContribuyente(naturalezaValue === NaturalezaReceptorEnum.Contribuyente);
    }, [naturalezaValue]);

    useEffect(() => {
        if (tipoDocumentoValue !== undefined && tipoDocumentoValue !== null) {
            setTipoDocumento(tipoDocumentoValue);
        }
    }, [tipoDocumentoValue]);

    const contribuyenteExists = value !== null && value !== undefined && value.id !== form.getFieldValue("id");
    const esInnominado = tipoDocumento === TipoDocumentoIdentidadEnum.Innominado;
    const { code, handleClose } = useModalContext();

    return (
        <>
            <Form form={form} gap={3} {...props}>
                <Collapse in={contribuyenteExists && esContribuyente}>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        Ya existe un cliente con ese Numero de Documento. No se pueden repetir Numero de Documento.
                    </Alert>
                </Collapse>

                <Typography variant="h6" color="primary">Datos SIFEN</Typography>

                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                    <Form.Select
                        name="naturaleza"
                        label="Naturaleza *"
                        size="small"
                        sx={{ flex: 1 }}
                        onValueChange={v => handleNaturalezaChange(Number(v))}
                    >
                        <Form.Select.Option value={NaturalezaReceptorEnum.Contribuyente}>Contribuyente</Form.Select.Option>
                        <Form.Select.Option value={NaturalezaReceptorEnum.NoContribuyente}>No Contribuyente</Form.Select.Option>
                    </Form.Select>

                    <Form.Select name="tipoOperacion" label="Tipo de Operación" size="small" sx={{ flex: 1 }}>
                        <Form.Select.Option value={TipoOperacionEnum.B2B}>B2B (Empresa a Empresa)</Form.Select.Option>
                        <Form.Select.Option value={TipoOperacionEnum.B2C}>B2C (Consumidor Final)</Form.Select.Option>
                        <Form.Select.Option value={TipoOperacionEnum.B2G}>B2G (Gobierno)</Form.Select.Option>
                        <Form.Select.Option value={TipoOperacionEnum.B2F}>B2F (Extranjero)</Form.Select.Option>
                    </Form.Select>
                </Stack>

                {
                    esContribuyente ? (
                        <>
                            <form.Field name="tipoContribuyente">
                                {() => (
                                    <Stack gap={0.5}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                            <Form.Select
                                                name="tipoContribuyente"
                                                label="Tipo de Contribuyente *"
                                                size="small"
                                                sx={{ flex: 1 }}
                                            >
                                                <Form.Select.Option value={TipoContribuyenteEnum.PersonaFisica}>Persona Física</Form.Select.Option>
                                                <Form.Select.Option value={TipoContribuyenteEnum.PersonaJuridica}>Persona Jurídica</Form.Select.Option>
                                            </Form.Select>
                                        </Stack>
                                    </Stack>
                                )}
                            </form.Field>

                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                <Form.TextField
                                    name="ruc"
                                    label="RUC *"
                                    size="small"
                                    onChange={e => fetch(e.target.value)}
                                    slotProps={{
                                        input: { endAdornment: loading && <CircularProgress size={16} /> },
                                        htmlInput: { maxLength: 8 }
                                    }}
                                    helperText="Entre 3 y 8 caracteres"
                                    sx={{ flex: 1 }}
                                />
                                <Form.TextField
                                    name="dv"
                                    label="DV *"
                                    size="small"
                                    sx={{ maxWidth: 100 }}
                                    slotProps={{ htmlInput: { maxLength: 1 } }}
                                />
                            </Stack>
                        </>
                    ) : (
                        <>
                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                <Form.Select
                                    name="tipoDocumento"
                                    label="Tipo de Documento *"
                                    size="small"
                                    sx={{ flex: 1 }}
                                    onValueChange={v => handleTipoDocumentoChange(Number(v))}
                                >
                                    <Form.Select.Option value={TipoDocumentoIdentidadEnum.CedulaParaguaya}>Cédula Paraguaya</Form.Select.Option>
                                    <Form.Select.Option value={TipoDocumentoIdentidadEnum.Pasaporte}>Pasaporte</Form.Select.Option>
                                    <Form.Select.Option value={TipoDocumentoIdentidadEnum.CedulaExtranjera}>Cédula Extranjera</Form.Select.Option>
                                    <Form.Select.Option value={TipoDocumentoIdentidadEnum.CarnetResidencia}>Carnet de Residencia</Form.Select.Option>
                                    <Form.Select.Option value={TipoDocumentoIdentidadEnum.Innominado}>Innominado</Form.Select.Option>
                                </Form.Select>
                            </Stack>

                            <Form.TextField
                                name="numeroDocumento"
                                label={esInnominado ? "Número de Documento (0 para innominado)" : "Número de Documento *"}
                                size="small"
                                fullWidth
                                disabled={esInnominado}
                                helperText={esInnominado ? "Para documentos innominados se usa '0'" : undefined}
                                onChange={e => fetch(e.target.value)}
                            />
                        </>
                    )
                }

                <Typography variant="h6" color="primary" sx={{ mt: 2 }}>Información Personal</Typography>

                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                    <Form.TextField name="razonSocial" label="Razón Social / Nombre *" size="small" sx={{ flex: 1 }} />
                    <Form.TextField name="nombreFantasia" label="Nombre Fantasía" size="small" sx={{ flex: 1 }} />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                    <Form.TextField name="email" label="Email *" size="small" type="email" sx={{ flex: 1 }} />
                    <Form.TextField name="telefono" label="Teléfono" size="small" sx={{ flex: 1 }} />
                    <Form.TextField name="celular" label="Celular" size="small" sx={{ flex: 1 }} />
                </Stack>

                <Typography variant="h6" color="primary" sx={{ mt: 2 }}>Dirección</Typography>

                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                    <Form.TextField name="direccion" label="Dirección *" size="small" sx={{ flex: 1 }} />
                    <Form.NumberField name="numeroCasa" label="Número de Casa *" size="small" sx={{ maxWidth: 155 }} />
                </Stack>

                <Form.ModelComboBoxAsync
                    name="paisId"
                    label="País *"
                    get={getPais}
                    getAll={getAllPaises}
                    size="small"
                />

                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                    <form.Field name="departamentoId">
                        {(field) => (
                            <Stack gap={0.5} flex={1}>
                                <Stack direction="row" gap={1} alignItems="center" flex={1}>
                                    <Form.TextField
                                        name="departamentoSeleccionado"
                                        label="Departamento *"
                                        size="small"
                                        fullWidth
                                        disabled
                                        placeholder="Selecciona un departamento..."
                                    />
                                    <IconButton onClick={() => openModal("searchDepartamento")} color="primary" size="small">
                                        <SearchIcon />
                                    </IconButton>
                                </Stack>
                                {field.state.meta.errors.length > 0 && (
                                    <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                        {field.state.meta.errors[0]?.message}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </form.Field>
                </Stack >
                <form.Subscribe selector={state => state.values.departamentoId}>
                    {departamentoId => departamentoId && (
                        <Stack direction="row" gap={1} alignItems="center" flex={1}>
                            <form.Field name="ciudadId">
                                {(field) => (
                                    <Stack gap={0.5} flex={1}>
                                        <Stack direction="row" gap={1} alignItems="center" flex={1}>
                                            <Form.TextField
                                                name="ciudadSeleccionada"
                                                label="Ciudad *"
                                                size="small"
                                                fullWidth
                                                placeholder="Selecciona una ciudad..."
                                                disabled
                                            />
                                            <IconButton onClick={() => openModal("searchCiudad")} color="primary" size="small">
                                                <SearchIcon />
                                            </IconButton>
                                        </Stack>
                                        {field.state.meta.errors.length > 0 && (
                                            <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                                {field.state.meta.errors[0]?.message}
                                            </Typography>
                                        )}
                                    </Stack>
                                )}
                            </form.Field>
                        </Stack>
                    )}
                </form.Subscribe>
                <Form.TextField name="observaciones" label="Observaciones" size="small" multiline rows={2} fullWidth />

                <Stack direction="row" justifyContent="flex-end" gap={2}>
                    {code && <Form.Button onClick={handleClose} variant="outlined">Cancelar</Form.Button>}
                    <Form.SubmitButton variant="contained" disabled={contribuyenteExists && esContribuyente}>
                        Guardar Cliente
                    </Form.SubmitButton>
                </Stack>
            </Form >

            <Dialog code="searchDepartamento" size="md">
                <ModalHeader>Seleccionar Departamento</ModalHeader>
                <ModalContextExtractor>
                    {({ handleClose }) => (
                        <DepartamentoManualSearch
                            padding={3}
                            onDepartamentoSelect={(departamento) => {
                                handleDepartamentoSelect(departamento);
                                handleClose();
                            }}
                        />
                    )}
                </ModalContextExtractor>
            </Dialog>

            <Dialog code="searchCiudad" size="md">
                <ModalHeader>Seleccionar Ciudad</ModalHeader>
                <ModalContextExtractor>
                    {({ handleClose }) => (
                        <form.Subscribe selector={state => state.values.departamentoId}>
                            {departamentoId => (
                                <CiudadManualSearch
                                    departamentoId={departamentoId || undefined}
                                    padding={3}
                                    onCiudadSelect={(ciudad) => {
                                        handleCiudadSelect(ciudad);
                                        handleClose();
                                    }}
                                />
                            )}
                        </form.Subscribe>
                    )}
                </ModalContextExtractor>
            </Dialog>
        </>
    );
}